import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, CellProps, Column, HeaderGroup, Row, useTable } from 'react-table'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import LogoutIcon from '@mui/icons-material/Logout'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'

import CalendarIcon from '@/components/ui/icons/Calendar'
import ChipButton from '@/components/ui/buttons/ChipButton'
import DoctorIcon from '@/components/ui/icons/Doctor'

export type ConsultationData = {
  id: string
  added: boolean
  doctor?: string
  filesCount: number
  mesuresCount: number
  startDate: string
}

export type ExpertiseConsultationsDialogProps = {
  error?: boolean
  data: ConsultationData[]
  loading?: boolean
  onAdd?: (consultationId: string) => void
  onClose?: () => void
  onDelete?: (consultationId: string) => void
  onShow?: (consultationId: string) => void
  open: boolean
  rowsPerPageOptions?: number[]
}

export function ExpertiseConsultationsDialog(props: ExpertiseConsultationsDialogProps) {
  const { data, onAdd, onClose, onDelete, onShow, open } = props
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.expertises.table.header.doctor'),
        accessor: 'doctor',
        Cell: ({ value: doctor }: CellProps<ConsultationData, ConsultationData['doctor']>) => (
          <Stack direction="row" spacing={1} alignItems="center">
            {!!doctor ? (
              <>
                <DoctorIcon />
                <span>{doctor}</span>
              </>
            ) : (
              <Chip color="secondary" label={<span>{t('page.expertises.consultationLocale.label')}</span>} variant="filled" />
            )}
          </Stack>
        ),
      },
      {
        Header: t('page.expertises.table.header.startDate'),
        accessor: 'startDate',
        Cell: ({ value }: CellProps<ConsultationData, ConsultationData['startDate']>) => (
          <Stack alignItems="center" justifyContent="start" direction="row" spacing={1}>
            <CalendarIcon fontSize="small" />
            <Chip
              color="secondary"
              label={
                <span>
                  {dayjs(value).format('DD/MM/YYYY')} - <b>{dayjs(value).format('hh[H]mm')}</b>
                </span>
              }
              variant="filled"
            />
          </Stack>
        ),
      },
      {
        Header: t('page.expertises.table.header.mesures'),
        accessor: 'mesuresCount',
        Cell: ({ value }: CellProps<ConsultationData, ConsultationData['mesuresCount']>) => (
          <Stack alignItems="center" justifyContent="start" direction="row">
            <Typography color="#9a9a9a" fontWeight="bold">
              {value || '-'}
            </Typography>
          </Stack>
        ),
      },
      {
        Header: t('page.expertises.table.header.documents'),
        accessor: 'filesCount',
        Cell: ({ value }: CellProps<ConsultationData, ConsultationData['filesCount']>) => (
          <Stack alignItems="center" justifyContent="start" direction="row">
            <Typography color="#9a9a9a" fontWeight="bold">
              {value || '-'}
            </Typography>
          </Stack>
        ),
      },
    ] as Column<ConsultationData>[]
  }, [t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<ConsultationData>({
    columns,
    data,
  })

  const handleAdd = (row: Row<ConsultationData>) => () => {
    onAdd?.(row.original.id)
  }

  const handleDelete = (row: Row<ConsultationData>) => () => {
    onDelete?.(row.original.id)
  }

  const handleShow = (row: Row<ConsultationData>) => () => {
    onShow?.(row.original.id)
  }

  return (
    <Dialog fullWidth={true} maxWidth={'lg'} onClose={onClose} open={open}>
      <DialogTitle sx={{ fontSize: '1em' }}>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
          <p>{t('page.expertise.dialogs.consultations.title')}</p>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper} id="consultations-table">
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              {headerGroups.map((headerGroup: HeaderGroup<ConsultationData>) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: HeaderGroup<ConsultationData>) => {
                    return (
                      <TableCell {...column.getHeaderProps()} scope="col">
                        {column.render('Header')}
                      </TableCell>
                    )
                  })}
                  <TableCell align="right">{t('common:table.header.action')}</TableCell>
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row)
                return (
                  <TableRow {...row.getRowProps()} key={row.original.id}>
                    {row.cells.map((cell: Cell<ConsultationData>) => (
                      <TableCell {...cell.getCellProps()} align="left">
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                    <TableCell key={`cell_${row.index}_actions`} align="right">
                      <Stack justifyContent="flex-end" direction="row" spacing={1}>
                        {!row.original.added ? (
                          <Tooltip placement="top" title={t('common:button.add.tooltip')}>
                            <ChipButton color="info" onClick={handleAdd(row)}>
                              <AddRoundedIcon />
                            </ChipButton>
                          </Tooltip>
                        ) : (
                          <Tooltip placement="top" title={t('common:button.delete.tooltip')}>
                            <ChipButton color="info" onClick={handleDelete(row)}>
                              <RemoveRoundedIcon />
                            </ChipButton>
                          </Tooltip>
                        )}
                        <Tooltip placement="top" title={t('common:button.show.tooltip')}>
                          <ChipButton color="info" onClick={handleShow(row)}>
                            <VisibilityRoundedIcon />
                          </ChipButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button fullWidth onClick={onClose} color="error" variant="outlined">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
            <span></span>
            <p>{t('common:button.close.label')}</p>
            <LogoutIcon />
          </Stack>
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ExpertiseConsultationsDialog
